<template>
  <div v-if="series.length" class="py-3">
    <div class="d-flex flex-wrap align-items-baseline justify-content-between">
      <b-col>
        <div class="device-view__attr">
          {{ timestampFormatted }}
        </div>
        <div v-if="mappers[dataType]" class="device-view__attr">{{ mappers[dataType] }}</div>
      </b-col>
      <b-button class="waveform-csv" variant="rounded" @click="getFaultCSV">
        Download CSV
        <Icon name="download" />
      </b-button>
    </div>
    <highcharts :options="chartOptions" />
  </div>
</template>

<script>
import MomentTimezone from 'moment-timezone';
import config from '@/config';
import HighchartsMethods from '@/components/mixins/highcharts-methods';
import {
  TIME_FORMATS,
  FORMAT_YMD_HHMMSS_TIMEZONE,
  FORMAT__HHMMSS_xxx
} from '@/components/shared/date-format';
import { mapState } from 'vuex';
import FaultEventsChartMixin from '@/components/mixins/fault/fault-events-chart-mixin';

const MAPPERS = {
  current_voltage_wave: 'Current Voltage Waveform'
};
const RED = 'rgb(244, 44, 44)';
const GREEN = 'rgb(5, 195, 76)';
const MAPPED_NAMES = {
  currentWaveValue: {
    label: 'Current Waveform',
    unit: 'A',
    color: RED,
    yAxis: 0
  },
  voltageWaveValue: {
    label: 'Voltage Waveform',
    unit: 'V',
    color: GREEN,
    yAxis: 1
  }
};

export default {
  name: 'FaultWaveformChart',
  mixins: [HighchartsMethods, FaultEventsChartMixin],
  props: {
    id: String,
    timestamp: Number,
    startPoint: Number,
    dataType: String,
    interval: Number,
    timestampStart: Number,
    timestampEnd: Number,
    series: {
      type: Array,
      default: function() {
        return [];
      }
    },
    isWaveform: Boolean,
    createdAt: String
  },
  data() {
    return {
      mappers: MAPPERS
    };
  },
  computed: {
    ...mapState({
      ways: state => state.substationVisnetHub.visnetHub.ways
    }),
    arraysOfTicks() {
      return this.generateArraysOfTicks(this.series);
    },
    chartOptions() {
      const ways = this.ways;

      const faultEventChartOptions = {
        chart: {
          styledMode: false,
          type: 'line',
          height: '500px',
          redraw: true,
          animation: false,
          zoomType: 'xy',
          panning: true,
          panKey: 'shift',
          backgroundColor: null
        },
        xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: {
            hour: TIME_FORMATS.HOUR_MINUTE,
            day: TIME_FORMATS.HOUR_MINUTE
          },
          min: (this.startPoint || this.timestamp) * 1000,
          minorTickInterval: 'auto',
          labels: {
            rotation: -45
          },
          gridLineWidth: 0.5
        },
        yAxis: [
          {
            title: {
              text: `Current [${MAPPED_NAMES.currentWaveValue.unit}]`
            },
            labels: {
              format: '{value}'
            },
            gridLineWidth: 0.5,
            showEmpty: false,
            tickPositions: this.arraysOfTicks.current
          },
          {
            title: {
              text: `Voltage [${MAPPED_NAMES.voltageWaveValue.unit}]`
            },
            labels: {
              format: '{value}'
            },
            opposite: true,
            gridLineWidth: 0.5,
            showEmpty: false,
            tickPositions: this.arraysOfTicks.voltage
          }
        ],
        plotOptions: {
          series: {
            pointStart: (this.startPoint || this.timestamp) * 1000,
            pointInterval: this.interval * 1000
          }
        },
        time: {
          timezoneOffset: -1 * MomentTimezone(this.timestamp * 1000 || this.createdAt).utcOffset()
        },
        series: this.series,
        tooltip: {
          formatter() {
            const time = MomentTimezone(this.x).format(FORMAT__HHMMSS_xxx);
            const { name } = this.series;
            const { color } = this.series;
            const unit = this.series.userOptions.suffix;
            const foundFeeder = ways[this.series.userOptions.feederNumber - 1];
            const feederName = (foundFeeder && `Feeder Name: ${foundFeeder.name}`) || '';

            return `<span style="color:${color}">\u25CF</span>
                    <b>${name}</b><br />
                    value: ${this.y} <i>[${unit}]</i><br />
                    time: ${time} <br/>
                    ${feederName}`;
          }
        }
      };

      return this.generateChart(faultEventChartOptions);
    },
    timestampFormatted() {
      const selectedTime = this.timestamp * 1000 || this.createdAt;

      return MomentTimezone(selectedTime).format(FORMAT_YMD_HHMMSS_TIMEZONE);
    }
  },
  methods: {
    generateFaultEventCSVPath() {
      const { externalVisnetHubId } = this.$route.params;

      return `${config.API_URL}/api/v3/visnet_hubs/${externalVisnetHubId}/fault_event_waveforms/${this.id}/csv`;
    }
  }
};
</script>
